import { useAsyncStorage } from '@euler/functions/useAsyncStorage';
import { useCallback, useEffect, useState } from 'react';
import { useWindowDimensions } from 'react-native';

export type SidebarState = 'collapsed' | 'floating' | 'expanded';

// Auto-collapse at 1200 and below; above that the sidebar opens by
// default. Catches iPad landscape and small-laptop windows.
const kAutoCollapseMaxWidth = 1200;
const kStorageKeyWide = 'sidebar:state-wide';

/**
 * Sidebar state machine.
 *
 * Narrow (≤1024):
 *   - Defaults to `collapsed` (icon rail).
 *   - User clicking the toggle cycles
 *     `collapsed → floating → expanded → collapsed`.
 *     Floating sits on top of the content (rail stays in the row, so
 *     nothing shifts). Expanding pins it back into the row and pushes
 *     content aside.
 *   - The narrow override is session-only — reload or widening past
 *     1024 resets to `collapsed`.
 *
 * Wide (>1024):
 *   - Defaults to `expanded`.
 *   - Toggle cycles `expanded ↔ collapsed` (no floating — there's
 *     room to keep it pinned).
 *   - Preference persists in storage.
 */
export const useSidebarState = () => {
  const dimens = useWindowDimensions();
  const isNarrow = dimens.width <= kAutoCollapseMaxWidth;

  const [storedWide, setStoredWide] = useAsyncStorage<SidebarState | null>(
    kStorageKeyWide,
  );
  const [narrowOverride, setNarrowOverride] = useState<SidebarState | null>(
    null,
  );

  // Clear the transient narrow override when leaving the narrow range.
  useEffect(() => {
    if (!isNarrow) setNarrowOverride(null);
  }, [isNarrow]);

  const state: SidebarState = isNarrow
    ? narrowOverride ?? 'collapsed'
    : storedWide === 'collapsed'
    ? 'collapsed'
    : 'expanded';

  const cycle = useCallback(() => {
    if (isNarrow) {
      setNarrowOverride(prev => {
        const curr = prev ?? 'collapsed';
        if (curr === 'collapsed') return 'floating';
        if (curr === 'floating') return 'expanded';
        return 'collapsed';
      });
    } else {
      setStoredWide(prev => (prev === 'collapsed' ? 'expanded' : 'collapsed'));
    }
  }, [isNarrow, setStoredWide]);

  /** Dismiss the floating panel (used by backdrop click / Esc). */
  const closeFloating = useCallback(() => {
    if (isNarrow) setNarrowOverride('collapsed');
  }, [isNarrow]);

  return { state, isNarrow, cycle, closeFloating } as const;
};
